import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { listReturnUrl } from '../lib/listReturn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useCrms } from '../lib/lookups';
import { useAuth, can } from '../lib/auth';
import { BUSINESS_TYPES, REG_SECTIONS } from '../lib/registrationForm';

/** Every text field of the form; creditDays and crmUserId are sent as numbers. */
const KEYS = [
  'name', 'registrationId', 'legalName', 'businessType', 'mainContactName', 'mainContactDesignation',
  'email', 'phone', 'fax', 'description',
  'billingAddress', 'dispatchAddress', 'city', 'zip', 'state',
  'contactPersonName', 'contactPersonDept', 'contactPersonDesignation', 'contactPersonPhone', 'contactPersonEmail', 'contactPersonAltMobile',
  'contactPerson2Name', 'contactPerson2Dept', 'contactPerson2Designation', 'contactPerson2Phone', 'contactPerson2Email', 'contactPerson2AltMobile',
  'contactPerson3Name', 'contactPerson3Dept', 'contactPerson3Designation', 'contactPerson3Phone', 'contactPerson3Email', 'contactPerson3AltMobile',
  'contactPerson4Name', 'contactPerson4Dept', 'contactPerson4Designation', 'contactPerson4Phone', 'contactPerson4Email', 'contactPerson4AltMobile',
  'gstNo', 'panNo', 'bankName', 'bankBranch', 'bankAccountNo', 'bankIfsc', 'msmeNo', 'creditDays',
  'cin', 'tin', 'drugLic', 'cst',
  'crmUserId',
] as const;

type Key = typeof KEYS[number];
type ClientForm = Record<Key, string>;

const empty = Object.fromEntries(KEYS.map((k) => [k, ''])) as ClientForm;

/** A contact person's fields, with the longest each column holds. */
const CONTACT_PARTS = [
  ['Name', 'Contact Person Name', 100], ['Dept', 'Department', 100], ['Designation', 'Designation', 100],
  ['Phone', 'Phone Number', 50], ['Email', 'Email Address', 100], ['AltMobile', 'Alt. Mobile / Extension', 50],
] as const;
/** Contact Person 1 is the original, narrower columns; the 4th is new and wider. */
const CONTACT_MAX: Record<string, Partial<Record<string, number>>> = {
  contactPerson: { Name: 50, Email: 50 },
  contactPerson4: { Name: 200, Email: 200 },
};

export function ClientFormPage() {
  // Master records are readable office-wide, writable by Super Admin / VPPL.
  const { user } = useAuth();
  const canEditMasters = can(user, 'EDIT_CLIENTS');
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<ClientForm>(empty);
  const [error, setError] = useState<string | null>(null);

  const crms = useCrms();

  const existing = useQuery({
    enabled: isEdit,
    queryKey: ['client', id],
    queryFn: () => api<Record<string, unknown>>(`/api/clients/${id}`),
  });

  useEffect(() => {
    if (existing.data) {
      const d = existing.data;
      setForm(Object.fromEntries(KEYS.map((k) => [k, d[k] == null ? '' : String(d[k])])) as ClientForm);
    }
  }, [existing.data]);

  const save = useMutation({
    mutationFn: () =>
      api<{ id: string }>(isEdit ? `/api/clients/${id}` : '/api/clients', {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify({
          ...form,
          creditDays: form.creditDays ? Number(form.creditDays) : null,
          crmUserId: form.crmUserId ? Number(form.crmUserId) : null,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      if (isEdit) qc.invalidateQueries({ queryKey: ['client', id] });
      navigate('/clients');
    },
    onError: (e) => setError((e as Error).message),
  });

  function update(k: Key, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  /** A text field bound to `k`. */
  const field = (k: Key, label: string, max: number, more: Omit<FieldProps, 'label' | 'value' | 'onChange' | 'max'> = {}) => (
    <Field key={k} label={label} value={form[k]} onChange={(v) => update(k, v)} max={max} {...more} />
  );

  /** Two contact persons side by side, as the registration form has them. */
  const contactPair = (a: string, b: string) =>
    CONTACT_PARTS.flatMap(([part, label, max]) => [a, b].map((prefix, i) =>
      field(`${prefix}${part}` as Key, `${label} (${i + 1})`, CONTACT_MAX[prefix]?.[part] ?? max,
        part === 'Email' ? { type: 'email' } : {})));

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError('Client Name is required.');
      return;
    }
    save.mutate();
  }

  if (isEdit && existing.isLoading) {
    return <div className="p-6 text-slate-500">Loading…</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-brand">
          {isEdit ? `Edit Client — ${id}` : 'New Client'}
        </h1>
        <div className="flex items-center gap-4">
          {isEdit && <Link to={`/clients/${id}/print`} className="text-sm text-slate-600 hover:underline">Print Form</Link>}
          <Link to={listReturnUrl('/clients')} className="text-sm text-slate-600 hover:underline">← Back to list</Link>
        </div>
      </div>

      {!canEditMasters && (
        <div className="mb-4 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          View only — client records are maintained by Super Admin and VPPL.
        </div>
      )}

      <form className="card" onSubmit={onSubmit}>
        {/* One disabled fieldset: a field added later cannot stay editable. */}
        <fieldset disabled={!canEditMasters} className="disabled:opacity-100">
        <div className="card-header">{REG_SECTIONS.general}</div>
        <div className="card-body grid gap-4 md:grid-cols-2">
          {field('name', 'Client / Company Name *', 520, { autoFocus: true })}
          {field('registrationId', 'Registration ID', 100)}
          {field('legalName', 'Client Name (Legal Name)', 520)}
          {field('businessType', 'Business Type', 100, { suggestions: BUSINESS_TYPES })}
          {field('mainContactName', 'Contact Person Name', 200)}
          {field('mainContactDesignation', 'Designation', 100)}
          {field('email', 'Email', 50, { type: 'email' })}
          {field('phone', 'Phone', 50)}
          {field('fax', 'Fax', 50)}
          {field('description', 'Description', 5000)}
        </div>

        <div className="card-header mt-2">{REG_SECTIONS.address}</div>
        <div className="card-body grid gap-4 md:grid-cols-2">
          {field('billingAddress', 'Business / Billing Address', 5000, { textarea: true })}
          {field('dispatchAddress', 'Dispatch Address', 5000, { textarea: true })}
          {field('city', 'City', 100)}
          {field('zip', 'ZIP / Postal Code', 20)}
          {field('state', 'State / Province', 100)}
        </div>

        <div className="card-header mt-2">{REG_SECTIONS.primary}</div>
        <div className="card-body grid gap-4 md:grid-cols-2">
          {contactPair('contactPerson', 'contactPerson2')}
        </div>

        <div className="card-header mt-2">{REG_SECTIONS.secondary}</div>
        <div className="card-body grid gap-4 md:grid-cols-2">
          {contactPair('contactPerson3', 'contactPerson4')}
        </div>

        <div className="card-header mt-2">{REG_SECTIONS.tax}</div>
        <div className="card-body grid gap-4 md:grid-cols-2">
          {field('gstNo', 'GST No.', 50)}
          {field('panNo', 'PAN No.', 20)}
          {field('bankName', 'Bank Name', 200)}
          {field('bankBranch', 'Bank Branch', 200)}
          {field('bankAccountNo', 'Account Number', 50)}
          {field('bankIfsc', 'IFSC Code', 20)}
          {field('msmeNo', 'MSME', 100, { placeholder: 'Udyam registration no. — or No' })}
          {field('creditDays', 'Credit Terms (Days)', 4, { type: 'number', placeholder: 'e.g. 60' })}
        </div>

        <div className="card-header mt-2">Statutory Details</div>
        <div className="card-body grid gap-4 md:grid-cols-2">
          {field('cin', 'CIN Number', 50)}
          {field('drugLic', 'Drug Licence Number', 50)}
          {field('tin', 'TIN Number', 50)}
          {field('cst', 'CST Number', 50)}
        </div>

        <div className="card-header mt-2">Assignment</div>
        <div className="card-body grid gap-4 md:grid-cols-2">
          <div>
            <label className="label">Assigned CRM Operator</label>
            <select
              className="input bg-white"
              value={form.crmUserId}
              onChange={(e) => update('crmUserId', e.target.value)}
            >
              <option value="">No assigned CRM (Unassigned)</option>
              {crms.data?.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="px-4 pb-3">
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          </div>
        )}

        </fieldset>

        <div className="border-t border-slate-200 px-4 py-3 flex justify-end gap-2">
          <Link to={listReturnUrl('/clients')} className="btn-ghost">
            {canEditMasters ? 'Cancel' : 'Back'}
          </Link>
          {canEditMasters && (
            <button type="submit" className="btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  textarea?: boolean;
  autoFocus?: boolean;
  max?: number;
  placeholder?: string;
  suggestions?: readonly string[];
}

function Field({
  label, value, onChange, type = 'text', textarea, autoFocus, max, placeholder, suggestions,
}: FieldProps) {
  const listId = suggestions ? `suggest-${label.replace(/\W+/g, '-')}` : undefined;
  return (
    <div>
      <label className="label">{label}</label>
      {textarea ? (
        <textarea
          className="input min-h-[80px]"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={max}
        />
      ) : (
        <input
          className="input"
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          maxLength={type === 'number' ? undefined : max}
          min={type === 'number' ? 0 : undefined}
          placeholder={placeholder}
          list={listId}
        />
      )}
      {listId && (
        <datalist id={listId}>
          {suggestions!.map((s) => <option key={s} value={s} />)}
        </datalist>
      )}
    </div>
  );
}
